import { useEffect, useMemo, useRef, useState } from "react";
import { derivePriceTax, effectiveRate, type PriceTaxMode, type PriceTaxValue } from "../../util/priceTaxDerive.ts";
import { fmtPrice } from "../../util/pricingCascade.ts";
import s from "./priceTaxEntry.module.scss";

export const PRICETAX_LS_MODE = "webadmincc-pricetax-mode";
export const PRICETAX_LS_RATE = "webadmincc-pricetax-rate";
const LS_MODE = PRICETAX_LS_MODE;
const LS_RATE = PRICETAX_LS_RATE;

export function loadPriceTaxMode(): PriceTaxMode {
  const m = localStorage.getItem(LS_MODE);
  return m === "net" || m === "gross" || m === "direct" ? m : "direct";
}
const loadMode = loadPriceTaxMode;

const MODES: { key: PriceTaxMode; label: string; hint: string }[] = [
  { key: "direct", label: "Direct", hint: "Enter price and tax amounts directly" },
  { key: "net", label: "Net + rate", hint: "Net price + tax rate → tax (US style)" },
  { key: "gross", label: "Gross + rate", hint: "Total incl. tax + tax rate → price (EU style)" },
];

/**
 * Mode-switchable price/tax entry: Direct (price+tax), Net+rate (price+rate→tax),
 * or Gross+rate (total+rate→price). Emits the derived {price, tax} (4 dp) via
 * onChange, or null when inputs are incomplete. The chosen mode and last tax rate
 * persist in localStorage so the user's preference (e.g. EU gross) sticks across
 * the Add form and the grid/Changes calculator.
 */
export function PriceTaxEntry(props: {
  labelPrice?: string;
  labelTax?: string;
  initialPrice?: number | null;
  initialTax?: number | null;
  onChange: (v: PriceTaxValue | null) => void;
  /** When provided, mode is controlled by the parent and the internal mode tabs are hidden. */
  mode?: PriceTaxMode;
  onModeChange?: (m: PriceTaxMode) => void;
  /** When provided, the tax rate is owned by the parent, which renders the one rate input
   *  (e.g. next to ProductPriceEntry's mode selector). Net/Gross then show the computed
   *  tax AMOUNT read-only here instead of repeating an editable rate field. */
  rate?: string;
}) {
  const { labelPrice = "Price", labelTax = "Tax", initialPrice, initialTax, onChange } = props;
  const controlledMode = props.mode !== undefined;
  const controlledRate = props.rate !== undefined;
  const [modeUn, setModeUn] = useState<PriceTaxMode>(loadMode);
  const mode = props.mode ?? modeUn;
  const setMode = (m: PriceTaxMode) => { props.onModeChange ? props.onModeChange(m) : setModeUn(m); };
  const [priceIn, setPriceIn] = useState(initialPrice != null ? fmtPrice(initialPrice) : "");
  const [taxIn, setTaxIn] = useState(initialTax != null ? fmtPrice(initialTax) : "");
  const [totalIn, setTotalIn] = useState(
    initialPrice != null && initialTax != null ? fmtPrice(initialPrice + initialTax) : "",
  );
  const [rateUn, setRateUn] = useState(() => localStorage.getItem(LS_RATE) || "25%");
  const rate = props.rate ?? rateUn;

  useEffect(() => { if (!controlledMode) localStorage.setItem(LS_MODE, mode); }, [controlledMode, mode]);
  useEffect(() => { if (!controlledRate) localStorage.setItem(LS_RATE, rate); }, [controlledRate, rate]);

  const derived = useMemo(
    () => derivePriceTax(mode, { price: priceIn, tax: taxIn, total: totalIn, rate }),
    [mode, priceIn, taxIn, totalIn, rate],
  );

  // Emit on every change without making onChange a dep (parent callbacks aren't stable).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // A pure Entry-mode switch recomputes `derived` from the same inputs; it must NOT emit that
  // to the parent — the stored price/tax may change only on a real user edit. Skipping the
  // mode-triggered emit keeps a mode toggle non-destructive (it can't silently re-tax e.g. a
  // rental at the shared buy rate); a later real edit emits normally.
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode) { prevModeRef.current = mode; return; }
    onChangeRef.current(derived);
  }, [derived, mode]);

  const preview = useMemo(() => {
    if (!derived) return null;
    const r = mode === "direct" ? effectiveRate(derived.price, derived.tax) : null;
    const total = derived.price + derived.tax;
    const rateStr = mode === "direct" ? (r != null ? `${r}%` : "—") : rate.trim().endsWith("%") ? rate.trim() : `${rate.trim()}%`;
    return { price: derived.price, tax: derived.tax, total, rateStr };
  }, [derived, mode, rate]);

  /** Editable rate field — only when this component owns the rate (standalone use). */
  const rateField = (
    <label className={s.field}>
      <span>Tax rate</span>
      <input className={s.input} type="text" inputMode="decimal" placeholder="25%" value={rate} onChange={(e) => setRateUn(e.target.value)} />
    </label>
  );
  /** Read-only computed money value; blank (placeholder dash) until the inputs are complete. */
  const computedField = (label: string, v: number | null) => (
    <label className={s.field}>
      <span>{label}</span>
      <input className={s.inputReadOnly} type="text" readOnly aria-readonly="true" data-reveal="off"
        placeholder="—" value={v != null ? fmtPrice(v) : ""} />
    </label>
  );

  return (
    <div className={s.wrap}>
      {!controlledMode && (
      <div className={s.modes} role="tablist">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={mode === m.key}
            title={m.hint}
            className={mode === m.key ? s.modeActive : s.mode}
            onClick={() => setMode(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>
      )}

      <div className={s.fields}>
        {mode === "direct" && (
          <>
            <label className={s.field}>
              <span>{labelPrice}</span>
              <input className={s.input} type="text" inputMode="decimal" placeholder="0.0000" value={priceIn} onChange={(e) => setPriceIn(e.target.value)} />
            </label>
            <label className={s.field}>
              <span>{labelTax}</span>
              <input className={s.input} type="text" inputMode="decimal" placeholder="0.0000" value={taxIn} onChange={(e) => setTaxIn(e.target.value)} />
            </label>
          </>
        )}
        {mode === "net" && (
          <>
            <label className={s.field}>
              <span>Net {labelPrice.toLowerCase()}</span>
              <input className={s.input} type="text" inputMode="decimal" placeholder="0.0000" value={priceIn} onChange={(e) => setPriceIn(e.target.value)} />
            </label>
            {controlledRate ? (
              <>
                {computedField(labelTax, derived?.tax ?? null)}
                {computedField("Total incl. tax", derived ? derived.price + derived.tax : null)}
              </>
            ) : rateField}
          </>
        )}
        {mode === "gross" && (
          <>
            <label className={s.field}>
              <span>Total incl. tax</span>
              <input className={s.input} type="text" inputMode="decimal" placeholder="0.0000" value={totalIn} onChange={(e) => setTotalIn(e.target.value)} />
            </label>
            {controlledRate ? computedField(labelTax, derived?.tax ?? null) : rateField}
          </>
        )}
      </div>

      <div className={s.preview}>
        {preview ? (
          <>
            <span className={s.previewMain}>{labelPrice} <strong>{fmtPrice(preview.price)}</strong> · {labelTax} <strong>{fmtPrice(preview.tax)}</strong></span>
            <span className={s.previewMeta}>{preview.rateStr} · total {fmtPrice(preview.total)}</span>
          </>
        ) : (
          <span className={s.previewMeta}>Enter values to compute {labelPrice.toLowerCase()} &amp; {labelTax.toLowerCase()}</span>
        )}
      </div>
    </div>
  );
}
